<!--
  SupportDeskNewTicket.svelte — an agent opens a ticket on a customer's behalf.

  For requests that arrive by phone, chat or a colleague's inbox. No eligibility
  check applies — the agent has already decided the request is worth answering —
  but the requester must hold an account, since the ticket is theirs to read and
  reply to. Every support-enabled product is offered, not only the ones the agent
  personally holds a licence for.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { EmptyState, PageHeader, SupportTicketForm } from '../../components';
	import { createDeskTicket, getSupportOverview } from '../../AppNetwork';
	import type { SupportProduct, SupportTicketInput } from '../../AppTypes';
	import { push } from '../../abstract-spa-router';
	import { toastError, toastSuccess } from '../../toast';
	import { t, t2 } from '../../i18n';

	/** Every support-enabled product. */
	let products: SupportProduct[] = $state([]);
	/** Whether the product list is loading. */
	let loading = $state(true);
	/** Failure of the product list. */
	let error = $state('');
	/** Whether the ticket is being submitted. */
	let submitting = $state(false);
	/** Fraction of the attachments uploaded, while a submit with files is in flight; `null` otherwise. */
	let uploadProgress = $state<number | null>(null);

	/** Loads every support-enabled product. */
	async function load(): Promise<void> {
		loading = true;
		error = '';
		/** The overview lists every support-enabled product, whatever the caller's own eligibility. */
		try {
			products = (await getSupportOverview()).products;
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load support');
		} finally {
			loading = false;
		}
	}

	/** Files the ticket and opens it in the desk. */
	async function submit(input: SupportTicketInput & { requesterEmail?: string }, files: File[]): Promise<void> {
		submitting = true;
		/* Only an upload with files shows a bar; without, the button's spinner is enough. */
		uploadProgress = files.length > 0 ? 0 : null;
		const onProgress = files.length > 0 ? (fraction: number) => (uploadProgress = fraction) : undefined;
		/** A requester without an account comes back as the error to show. */
		try {
			const ticket = await createDeskTicket({ ...input, requesterEmail: input.requesterEmail ?? '' }, files, onProgress);
			toastSuccess(t2('Ticket {{key}} opened.', { key: ticket.ticketKey }));
			push(`/Support/Desk/Tickets/${ticket.ticketKey}`);
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to open the ticket'));
		} finally {
			submitting = false;
			uploadProgress = null;
		}
	}

	onMount(load);
</script>

<PageHeader
	title={$t('New ticket on behalf of a customer')}
	description={$t('For requests that reached the team another way — by phone, chat or email.')}
	breadcrumbs={[{ label: $t('Support Desk'), href: '/Support/Desk' }, { label: $t('New ticket') }]}
/>

{#if loading}
	<EmptyState variant="loading" />
{:else if error}
	<EmptyState variant="error" description={error} />
{:else if products.length === 0}
	<EmptyState
		faIcon="life-ring"
		title={$t('No product has support enabled')}
		description={$t('Enable support for a product under Settings → Admin → Support.')}
	/>
{:else}
	<AbstractCard class="max-w-none">
		<SupportTicketForm {products} onBehalf {submitting} {uploadProgress} cancelHref="/Support/Desk" onsubmit={submit} />
	</AbstractCard>
{/if}
